export const SOAP_SECTION_LABELS = [
  "Subjective",
  "Objective",
  "Assessment",
  "Plan",
] as const;

export type SoapSectionLabel = (typeof SOAP_SECTION_LABELS)[number];

export interface SoapNoteSection {
  /** Positional (`section-3`), never built from heading text. */
  key: string;
  /** Display heading, sentence-cased when the note shouts it in capitals. */
  heading: string;
  /** The SOAP label this heading maps to, including repeats. */
  soap: SoapSectionLabel | null;
  /** True for the first heading of a SOAP label: the one the jump bar targets. */
  target: boolean;
  /** The section body, without its heading line. */
  text: string;
}

export interface SplitSoapNote {
  /** Text before the first section; the whole note when it has no headings. */
  intro: string;
  sections: SoapNoteSection[];
}

/**
 * Splits a signed note at its top-level headings. Level-2 headings, any other
 * level-1 heading, and legacy bare SOAP lines are sections; level-3 and deeper
 * headings stay in their section's body. A leading level-1 note title is
 * dropped. Keys are positional because heading text can carry clinical content.
 */
export function splitSoapNoteSections(content: string): SplitSoapNote {
  const lines = scanLines(content);
  const titleIndex = noteTitleIndex(lines);
  const intro: string[] = [];
  const drafts: SectionDraft[] = [];
  const seen = new Set<SoapSectionLabel>();

  lines.forEach((line, index) => {
    if (index === titleIndex) return;

    if (line.heading?.topLevel) {
      const soap = line.heading.soap;
      drafts.push({
        key: `section-${drafts.length}`,
        heading: soap ?? sentenceCaseHeading(line.heading.label),
        soap,
        target: soap !== null && !seen.has(soap),
        lines: [],
      });
      if (soap) seen.add(soap);

      return;
    }

    (drafts.at(-1)?.lines ?? intro).push(line.text);
  });

  return {
    intro: trimBlankEdges(intro),
    sections: drafts.map(({ lines: body, ...section }) => ({
      ...section,
      text: trimBlankEdges(body),
    })),
  };
}

interface SectionDraft extends Omit<SoapNoteSection, "text"> {
  lines: string[];
}

/** Shouted headings (SUBJECTIVE) become sentence case; mixed case is kept. */
export function sentenceCaseHeading(text: string): string {
  const letters = text.replace(/[^\p{L}]/gu, "");
  const shouting =
    letters.length >= 4 &&
    letters === letters.toUpperCase() &&
    letters !== letters.toLowerCase();
  if (!shouting) return text;

  const lower = text.toLowerCase();

  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function scrollToSoapNoteSection(
  target:
    | { scrollIntoView(options: { block: "start"; behavior: "auto" | "smooth" }): void }
    | undefined,
  reduceMotion: boolean
): boolean {
  if (!target) return false;

  target.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });

  return true;
}

interface LineHeading {
  /** 1 to 6 for ATX headings, 0 for a legacy bare SOAP line. */
  level: number;
  label: string;
  soap: SoapSectionLabel | null;
  topLevel: boolean;
}

interface ScannedLine {
  text: string;
  heading: LineHeading | null;
}

interface CodeFence {
  marker: string;
  length: number;
}

function scanLines(content: string): ScannedLine[] {
  const seenBare = new Set<SoapSectionLabel>();
  let fence: CodeFence | null = null;
  let listIndent: number | null = null;

  return content.split(/\r?\n/).map((text) => {
    const wasFenced = fence !== null;
    fence = nextFence(text, fence);
    listIndent = nextListIndent(text, listIndent);
    const inCode = wasFenced || fence !== null;
    const heading = inCode || listIndent !== null ? null : parseHeading(text);

    if (heading && heading.level === 0 && heading.soap) {
      if (seenBare.has(heading.soap)) return { text, heading: null };

      seenBare.add(heading.soap);
    }

    return { text, heading };
  });
}

function parseHeading(line: string): LineHeading | null {
  if (/^(?: {4}|\t)/.test(line)) return null;

  const atx = /^ {0,3}(#{1,6})[ \t]+(.+?)\s*$/.exec(line);
  const raw = atx ? (atx[2] ?? "").replace(/[ \t]+#+$/, "") : line;
  const soap = soapLabel(raw);
  const label = soap ?? cleanHeadingText(raw);

  if (!label || (!atx && !soap)) return null;

  const level = atx ? (atx[1] ?? "").length : 0;

  return { level, label, soap, topLevel: level <= 2 };
}

function cleanHeadingText(raw: string): string {
  return raw
    .trim()
    .replace(/\*\*|__/g, "")
    .replace(/\\([!"#$%&'()*+,\-./:;<=>?@[\]\\^_`{|}~])/g, "$1");
}

function soapLabel(line: string): SoapSectionLabel | null {
  const bare = line
    .trim()
    .replace(/^[*_#\s]+|[*_:\s]+$/g, "")
    .toLowerCase();

  return SOAP_SECTION_LABELS.find((label) => bare === label.toLowerCase()) ?? null;
}

/**
 * A leading level-1 heading is the note title: the first level-1 heading counts
 * only when no level-2 heading and no prose line (non-blank, non-heading)
 * precedes it. After intro text it is an ordinary section heading.
 */
function noteTitleIndex(lines: ScannedLine[]): number {
  const first = lines.findIndex((line) => line.heading?.level === 1);
  if (first < 0) return -1;

  if (lines[first]?.heading?.soap) return -1;

  const before = lines.slice(0, first);
  const level2Before = before.some((line) => line.heading?.level === 2);
  const proseBefore = before.some((line) => !line.heading && line.text.trim() !== "");

  return level2Before || proseBefore ? -1 : first;
}

function trimBlankEdges(lines: string[]): string {
  return lines.join("\n").replace(/^\n+|\n+$/g, "");
}

function nextListIndent(line: string, current: number | null): number | null {
  if (!line.trim()) return current;

  const marker = /^([ \t]*)(?:[-+*]|\d+[.)])[ \t]+/.exec(line);

  if (marker) {
    const indent = (marker[1] ?? "").length;

    return current === null ? indent : Math.min(current, indent);
  }

  const indentation = line.length - line.trimStart().length;

  return current !== null && indentation > current ? current : null;
}

function nextFence(line: string, current: CodeFence | null): CodeFence | null {
  const match = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);

  if (!match) return current;

  const run = match[1] ?? "";
  const marker = run.charAt(0);

  if (!current) return { marker, length: run.length };

  const sameFence = marker === current.marker && run.length >= current.length;

  return sameFence && (match[2] ?? "").trim() === "" ? null : current;
}
